/* =========================================================
   DRCHOB Voucher Auto-Swap (Clinic <-> Flower Delivery)
   GOAL:
   - If cart has ANY flower -> ensure voucher is the -flower-delivery version
   - If cart has NO flower -> ensure voucher is the clinic version
   - Block checkout until the cart is correct (prevents 2-shipment checkout)

   Clinic voucher handles:
     voucher200, voucher400, voucher600
   Flower-delivery voucher handles:
     voucher200-flower-delivery, voucher400-flower-delivery, voucher600-flower-delivery
   Flower handles:
     flower-(price) e.g. flower-300
   ========================================================= */

(function () {
  const DEBUG = false;

  const HANDLES = {
    clinic: {
      200: "voucher200",
      400: "voucher400",
      600: "voucher600",
    },
    flower: {
      200: "voucher200-flower-delivery",
      400: "voucher400-flower-delivery",
      600: "voucher600-flower-delivery",
    }
  };

  const DENOMS = [200, 400, 600];

  // ---------------------------
  // UI helper (optional)
  // ---------------------------
  let bannerEl = null;
  function showBanner(msg) {
    try {
      if (!bannerEl) {
        bannerEl = document.createElement("div");
        bannerEl.style.cssText =
          "position:fixed;left:16px;right:16px;bottom:16px;z-index:99999;" +
          "background:#0E2A37;color:#fff;padding:12px 14px;border-radius:10px;" +
          "box-shadow:0 10px 30px rgba(0,0,0,.25);font:600 14px/1.4 Raleway,system-ui;";
        document.body.appendChild(bannerEl);
      }
      bannerEl.textContent = msg;
      bannerEl.style.display = "block";
    } catch (_) {}
  }
  function hideBanner() {
    try { if (bannerEl) bannerEl.style.display = "none"; } catch (_) {}
  }

  // ---------------------------
  // Detection
  // ---------------------------
  function isFlowerItem(item) {
    const handle = String(item.handle || "").toLowerCase();
    const type = String(item.product_type || "").toLowerCase();
    const vendor = String(item.vendor || "").toLowerCase();

    // Your strongest signal:
    if (handle.startsWith("flower-")) return true;

    // Backups:
    if (type.includes("flower")) return true;
    if (handle.includes("bouquet")) return true;
    if (vendor.includes("wassim")) return true;

    return false;
  }

  function voucherDenom(handle) {
    const h = String(handle || "").toLowerCase();
    const m = h.match(/^voucher(200|400|600)(-flower-delivery)?$/);
    return m ? Number(m[1]) : null;
  }

  function isAnyVoucher(handle) {
    return voucherDenom(handle) != null;
  }

  function desiredVoucherHandle(denom, hasFlower) {
    return hasFlower ? HANDLES.flower[denom] : HANDLES.clinic[denom];
  }

  // ---------------------------
  // Network helpers
  // ---------------------------
  async function getCart() {
    const r = await fetch("/cart.js?ts=" + Date.now(), { credentials: "same-origin" });
    return r.json();
  }

  async function changeByKey(key, quantity) {
    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity })
    });
    return r.json();
  }

  async function addVariant(id, quantity) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id, quantity })
    });

    if (!r.ok) {
      let msg = "Add to cart failed (" + r.status + ")";
      try {
        const j = await r.json();
        if (j && (j.description || j.message)) msg = j.description || j.message;
      } catch (_) {}
      throw new Error(msg);
    }
    return r.json();
  }

  const variantCache = new Map();

  async function variantIdByHandle(handle) {
    const h = String(handle || "").toLowerCase();
    if (variantCache.has(h)) return variantCache.get(h);

    const r = await fetch("/products/" + encodeURIComponent(h) + ".js?ts=" + Date.now(), {
      credentials: "same-origin"
    });
    if (!r.ok) throw new Error("Could not load /products/" + h + ".js (" + r.status + ")");

    const p = await r.json();
    const v = p && p.variants && p.variants[0];
    if (!v || !v.id) throw new Error("No variant id for handle: " + h);

    const id = Number(v.id);
    variantCache.set(h, id);
    return id;
  }

  // ---------------------------
  // Core enforcement (single source of truth)
  // ---------------------------
  let running = false;
  let pending = false;

  async function enforceOnce() {
    const cart = await getCart();
    const items = cart.items || [];

    const hasFlower = items.some(isFlowerItem);

    if (DEBUG) {
      console.log("[DRCHOB] hasFlower:", hasFlower);
      console.table(items.map(i => ({ handle: i.handle, qty: i.quantity, key: i.key })));
    }

    for (const denom of DENOMS) {
      const desired = desiredVoucherHandle(denom, hasFlower).toLowerCase();

      // find all voucher lines of this denomination
      const voucherLines = items.filter(it => {
        if (!isAnyVoucher(it.handle)) return false;
        return voucherDenom(it.handle) === denom;
      });

      if (!voucherLines.length) continue;

      const totalQty = voucherLines.reduce((s, it) => s + (it.quantity || 0), 0);
      const allDesired = voucherLines.every(it => String(it.handle || "").toLowerCase() === desired);

      // already perfect (single line, correct handle, correct qty)
      if (allDesired && voucherLines.length === 1 && voucherLines[0].quantity === totalQty) continue;

      // remove all denom voucher lines (key-based, stable)
      for (const line of voucherLines) {
        await changeByKey(line.key, 0);
      }

      // add desired voucher back
      const desiredId = await variantIdByHandle(desired);
      await addVariant(desiredId, totalQty);

      if (DEBUG) console.log("[DRCHOB] swapped denom", denom, "->", desired, "qty", totalQty);
    }

    return true;
  }

  async function enforceVoucherRule() {
    if (running) { pending = true; return; }
    running = true;

    try {
      await enforceOnce();
    } catch (e) {
      if (DEBUG) console.warn("[DRCHOB] enforce error:", e);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        setTimeout(() => enforceVoucherRule().catch(() => {}), 50);
      }
    }
  }

  async function ensureCartCorrectForCheckout() {
    // We’ll try a few times to settle the cart (handles async Shopify updates)
    showBanner("Updating your cart for the correct delivery method…");
    for (let i = 0; i < 3; i++) {
      await enforceOnce().catch(() => {});
      const cart = await getCart().catch(() => null);
      if (!cart) continue;

      const items = cart.items || [];
      const hasFlower = items.some(isFlowerItem);

      // If has flower, there must be NO clinic voucher handles
      if (hasFlower) {
        const hasClinicVoucher = items.some(it => {
          const h = String(it.handle || "").toLowerCase();
          return /^voucher(200|400|600)$/.test(h);
        });
        if (!hasClinicVoucher) { hideBanner(); return; }
      } else {
        // If no flower, there must be NO -flower-delivery vouchers
        const hasFlowerVoucher = items.some(it => String(it.handle || "").toLowerCase().endsWith("-flower-delivery"));
        if (!hasFlowerVoucher) { hideBanner(); return; }
      }

      await new Promise(r => setTimeout(r, 150));
    }

    // If still not correct, block checkout (prevents 2-shipment checkout)
    showBanner("Cart update didn’t complete. Please wait 2 seconds and click checkout again.");
    throw new Error("Cart did not settle to desired voucher state");
  }

  // ---------------------------
  // Checkout interception (AGGRESSIVE)
  // ---------------------------
  let redirecting = false;
  let nativeAssign = null;
  let nativeReplace = null;

  try {
    nativeAssign = window.location.assign.bind(window.location);
    nativeReplace = window.location.replace.bind(window.location);
  } catch (_) {}

  async function goCheckout() {
    if (redirecting) return;
    redirecting = true;

    try {
      await ensureCartCorrectForCheckout();
      // Send to /checkout (Shopify will route to the active checkout)
      if (nativeAssign) nativeAssign("/checkout?ts=" + Date.now());
      else window.location.href = "/checkout?ts=" + Date.now();
    } catch (e) {
      if (DEBUG) console.error("[DRCHOB] checkout blocked:", e);
      redirecting = false; // allow retry
    }
  }

  // Capture clicks on checkout triggers (cart, drawer, custom buttons, etc.)
  document.addEventListener("click", function (e) {
    const el = e.target.closest(
      'a[href*="/checkout"], a[href*="/checkouts/"], button[name="checkout"], input[name="checkout"], button[data-checkout]'
    );
    if (!el) return;

    // Avoid accelerated payment buttons (they bypass theme anyway)
    if (el.closest(".shopify-payment-button")) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    goCheckout();
  }, true);

  // Capture cart form submits
  document.addEventListener("submit", function (e) {
    const form = e.target;
    if (!form) return;

    const action = String(form.getAttribute("action") || "");
    const hasCheckoutBtn = form.querySelector('button[name="checkout"], input[name="checkout"]');

    if (action.includes("/cart") && hasCheckoutBtn) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      goCheckout();
    }
  }, true);

  // Intercept programmatic form.submit() (some custom scripts do this)
  const nativeFormSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function () {
    try {
      const action = String(this.getAttribute("action") || "");
      const hasCheckoutBtn = this.querySelector && this.querySelector('button[name="checkout"], input[name="checkout"]');
      if (action.includes("/cart") && hasCheckoutBtn) {
        goCheckout();
        return;
      }
    } catch (_) {}
    return nativeFormSubmit.apply(this, arguments);
  };

  // Intercept location.assign/replace if other code calls it
  try {
    window.location.assign = function (url) {
      const u = String(url || "");
      if (!redirecting && (u.includes("/checkout") || u.includes("/checkouts/"))) {
        goCheckout();
        return;
      }
      return nativeAssign ? nativeAssign(url) : (window.location.href = url);
    };
    window.location.replace = function (url) {
      const u = String(url || "");
      if (!redirecting && (u.includes("/checkout") || u.includes("/checkouts/"))) {
        goCheckout();
        return;
      }
      return nativeReplace ? nativeReplace(url) : (window.location.href = url);
    };
  } catch (_) {}

  // Re-run after any ajax cart changes
  const _fetch = window.fetch;
  window.fetch = async function () {
    const res = await _fetch.apply(this, arguments);
    try {
      const url = String(arguments[0] || "");
      if (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/update") || url.includes("/cart/clear")) {
        setTimeout(() => enforceVoucherRule().catch(() => {}), 120);
      }
    } catch (_) {}
    return res;
  };

  // Initial run
  document.addEventListener("DOMContentLoaded", () => {
    enforceVoucherRule().catch(() => {});
  });

  // Optional manual hook
  window.DRCHOB_EnforceVoucherRule = enforceVoucherRule;
  window.DRCHOB_GoCheckout = goCheckout;

})();
